import React, { useEffect, useState } from "react";
import { TrendingUp, ArrowRight, Quote, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, Cell,
} from "recharts";
import { sr } from "../../../lib/sr";

const NAVY = "#0A0E1A";
const ORANGE = "#E8710A";
const GOLD = "#F2A33D";
const BLUE = "#2E67F8";

function Stat({ s }) {
  return (
    <div className="sr-card" data-testid={`ss-stat-${s.label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
      style={{ padding: 16 }}>
      <div className="sr-muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5 }}>{s.label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 8 }}>
        <span style={{ color: "#9aa7b8", fontSize: 15, textDecoration: "line-through" }}>{s.before}</span>
        <ArrowRight size={14} style={{ color: "var(--muted)" }} />
        <span style={{ color: NAVY, fontSize: 24, fontWeight: 800 }}>{s.after}</span>
      </div>
      <span style={{ display: "inline-block", marginTop: 8, background: "#e9f9ef", color: "#127a3e",
        border: "1px solid #b7ecc9", borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 800 }}>{s.delta}</span>
    </div>
  );
}

export default function SuccessStory() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { sr.successStory().then(setD).catch(() => setErr(true)); }, []);

  if (err) return <div className="sr-empty">Couldn't load the success story.</div>;
  if (!d) return <div className="sr-empty" data-testid="ss-loading"><Loader2 className="sr-spin" /></div>;

  return (
    <div data-testid="success-story-page" style={{ maxWidth: 1080, margin: "0 auto", paddingBottom: 40 }}>
      {/* HERO */}
      <div style={{ background: `radial-gradient(900px 400px at 85% -20%, #1c2b4a 0%, ${NAVY} 60%)`,
        color: "#fff", borderRadius: 18, padding: "30px 30px 26px", position: "relative", overflow: "hidden" }}>
        <span className="sr-badge" style={{ background: "rgba(242,163,61,.18)", color: GOLD, border: `1px solid ${GOLD}` }}
          data-testid="ss-sample-badge"><Sparkles size={12} /> Sample success story</span>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: `linear-gradient(135deg, ${GOLD}, ${ORANGE})`,
            display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 24 }}>{d.client.initial}</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{d.client.name}</div>
            <div style={{ color: "#cfd8e6", fontSize: 13 }}>{d.client.category} · {d.client.location} · {d.client.tenure}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 18, marginTop: 22, flexWrap: "wrap" }}>
          <div style={{ fontSize: 72, lineHeight: 1, fontWeight: 900, color: GOLD }} data-testid="ss-headline-metric">
            <TrendingUp size={44} style={{ verticalAlign: "middle", marginRight: 8 }} />{d.headline.metric}
          </div>
          <div style={{ paddingBottom: 8 }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{d.headline.label}</div>
            <div style={{ color: "#cfd8e6", fontSize: 13 }}>{d.headline.period}</div>
          </div>
        </div>
        <p style={{ color: "#e6edf6", maxWidth: 620, marginTop: 14, fontSize: 14 }}>{d.headline.sub}</p>
      </div>

      {/* CHALLENGE + APPROACH */}
      <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1.4fr", gap: 16, marginTop: 18 }}>
        <div className="sr-card">
          <h3 style={{ margin: "0 0 8px", color: NAVY, fontSize: 15 }}>The challenge</h3>
          <p className="sr-muted" style={{ fontSize: 14, margin: 0 }}>{d.challenge}</p>
        </div>
        <div className="sr-card">
          <h3 style={{ margin: "0 0 10px", color: NAVY, fontSize: 15 }}>What SmartReachAI did</h3>
          {d.approach.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <CheckCircle2 size={16} style={{ color: "#127a3e", flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: 13.5, color: NAVY }}>{a}</span>
            </div>
          ))}
        </div>
      </div>

      {/* BEFORE / AFTER STATS */}
      <h3 style={{ color: NAVY, margin: "24px 0 12px", fontSize: 16 }}>Before → after</h3>
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
        {d.before_after.map((s, i) => <Stat key={i} s={s} />)}
      </div>

      {/* REVENUE CHART */}
      <div className="sr-card" style={{ marginTop: 20 }}>
        <h3 style={{ color: NAVY, margin: "0 0 4px", fontSize: 16 }}>Monthly revenue growth</h3>
        <p className="sr-muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>
          Actual revenue with SmartReachAI vs. the flat baseline before we started ($ thousands / month).
        </p>
        <div style={{ width: "100%", height: 300 }} data-testid="ss-revenue-chart">
          <ResponsiveContainer>
            <LineChart data={d.revenue_series} margin={{ top: 6, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#5b6b83" }} />
              <YAxis tick={{ fontSize: 12, fill: "#5b6b83" }} tickFormatter={(v) => `$${v}k`} />
              <Tooltip formatter={(v, n) => [`$${v}k`, n === "revenue" ? "With SmartReach" : "Baseline"]} />
              <Legend formatter={(v) => (v === "revenue" ? "With SmartReach" : "Baseline (no SmartReach)")} />
              <Line type="monotone" dataKey="baseline" stroke="#9aa7b8" strokeWidth={2} strokeDasharray="5 5" dot={false} />
              <Line type="monotone" dataKey="revenue" stroke={ORANGE} strokeWidth={3}
                dot={{ r: 4, fill: ORANGE }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* CHANNEL CONTRIBUTION */}
      <div className="sr-card" style={{ marginTop: 20 }}>
        <h3 style={{ color: NAVY, margin: "0 0 4px", fontSize: 16 }}>Where the growth came from</h3>
        <p className="sr-muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>Attributed monthly revenue by channel ($ thousands), with return on ad spend.</p>
        <div style={{ width: "100%", height: 260 }} data-testid="ss-channel-chart">
          <ResponsiveContainer>
            <BarChart data={d.channels} margin={{ top: 6, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5b6b83" }} interval={0} angle={-12} textAnchor="end" height={54} />
              <YAxis tick={{ fontSize: 12, fill: "#5b6b83" }} tickFormatter={(v) => `$${v}k`} />
              <Tooltip formatter={(v, n, p) => [`$${v}k · ${p.payload.roas}x ROAS`, "Revenue"]} />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                {d.channels.map((c, i) => <Cell key={i} fill={[ORANGE, GOLD, BLUE, "#7b8ba0", "#127a3e"][i % 5]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* TIMELINE */}
      <div className="sr-card" style={{ marginTop: 20 }}>
        <h3 style={{ color: NAVY, margin: "0 0 12px", fontSize: 16 }}>How it unfolded</h3>
        {d.timeline.map((t, i) => (
          <div key={i} style={{ display: "flex", gap: 12, paddingBottom: 12 }} data-testid={`ss-timeline-${i}`}>
            <div style={{ flexShrink: 0, width: 74, textAlign: "right", fontWeight: 800, color: ORANGE, fontSize: 12.5 }}>{t.when}</div>
            <div style={{ borderLeft: "2px solid #eef1f5", paddingLeft: 12, fontSize: 13.5, color: NAVY }}>{t.event}</div>
          </div>
        ))}
      </div>

      {/* TESTIMONIAL */}
      <div className="sr-card" style={{ marginTop: 20, background: "#fffaf3", border: `1px solid #ffd9ab` }}>
        <Quote size={22} style={{ color: ORANGE }} />
        <p style={{ fontSize: 17, color: NAVY, fontStyle: "italic", margin: "8px 0 12px", lineHeight: 1.5 }} data-testid="ss-testimonial">
          “{d.testimonial.quote}”
        </p>
        <div style={{ fontWeight: 800, color: NAVY }}>{d.testimonial.author}</div>
        <div className="sr-muted" style={{ fontSize: 13 }}>{d.testimonial.role}</div>
      </div>

      <p className="sr-muted" style={{ fontSize: 11.5, textAlign: "center", marginTop: 18 }}>
        Illustrative sample based on typical SmartReachAI engagements. Individual results vary.
      </p>
    </div>
  );
}
